import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { FuentePatroclo, PasoPatroclo } from '@manejo-alcanza/contracts';
import { ErrorApi, preguntarAPatroclo } from '../../api';
import { Aviso, Error_ } from '../basicos';
import { LogoPatroclo } from './LogoPatroclo';
import { MensajeAsistente } from './MensajeAsistente';
import { pausaDe, type Pausa } from './pausa';

/**
 * Patroclo a mano: el botón de abajo a la derecha y un chat pequeño.
 *
 * *(Pedro, 22/10/2026: «un botón abajo a la derecha que abre un mini chat con
 * Patroclo, y el chat ya grande en su propia página».)*
 *
 * Es **el mismo chat que la pestaña**, no otro: pregunta por
 * `preguntarAPatroclo` con la sesión y el CSRF de siempre, y la conversación
 * que empieza aquí queda en el historial. «Abrir en grande» la lleva tal cual
 * a `/patroclo/:id`, donde están el historial completo y los documentos.
 *
 * No se muestra:
 *
 * - **En la pestaña de Patroclo**, que ya es el chat grande.
 * - **Alistando una orden** (`/alistar/:id`): ahí la barra de despachar va
 *   pegada abajo, donde llega el pulgar, y el botón le taparía una esquina.
 *
 * Esconderlo no lo desmonta: la conversación sigue ahí al volver.
 */
interface MensajeVista {
  clave: string;
  rol: 'usuario' | 'asistente' | 'error';
  contenido: string;
  fuentes?: FuentePatroclo[];
  pasos?: PasoPatroclo[];
  rechazada?: boolean;
  tiempoMs?: number | null;
  idEjecucion?: string;
}

const SUGERENCIAS = ['¿Qué tenemos pendiente de facturar?', '¿Cuál fue el OTIF del mes pasado?'];

export function ChatFlotante(): ReactNode {
  const { pathname } = useLocation();
  const navegar = useNavigate();
  const cliente = useQueryClient();

  const [abierto, setAbierto] = useState(false);
  const [conversacionId, setConversacionId] = useState<string | undefined>(undefined);
  const [mensajes, setMensajes] = useState<MensajeVista[]>([]);
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const [segundosPensando, setSegundosPensando] = useState(0);
  const [pausa, setPausa] = useState<Pausa | null>(null);
  const [ahora, setAhora] = useState(Date.now());
  const fin = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLTextAreaElement>(null);

  const oculto = pathname.startsWith('/patroclo') || /^\/alistar\/[^/]+/.test(pathname);

  useEffect(() => {
    if (!pensando) return;
    setSegundosPensando(0);
    const t = setInterval(() => setSegundosPensando((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [pensando]);

  // La cuenta atrás de una pausa; al terminar, se levanta sola.
  useEffect(() => {
    if (!pausa?.hasta) return;
    const t = setInterval(() => {
      setAhora(Date.now());
      if (Date.now() >= pausa.hasta!) setPausa(null);
    }, 1000);
    return () => clearInterval(t);
  }, [pausa]);

  useEffect(() => {
    if (abierto) fin.current?.scrollIntoView({ block: 'end' });
  }, [mensajes, pensando, abierto]);

  useEffect(() => {
    if (abierto) entrada.current?.focus();
  }, [abierto]);

  // Escape cierra, como cualquier ventana que flota encima de la página.
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (ev: globalThis.KeyboardEvent): void => {
      if (ev.key === 'Escape') setAbierto(false);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierto]);

  if (oculto) return null;

  const enviar = async (crudo: string): Promise<void> => {
    const pregunta = crudo.trim();
    if (!pregunta || pensando || pausa) return;
    setTexto('');
    setMensajes((m) => [...m, { clave: `u-${Date.now()}`, rol: 'usuario', contenido: pregunta }]);
    setPensando(true);
    try {
      const r = await preguntarAPatroclo({
        pregunta,
        ...(conversacionId ? { conversacion_id: conversacionId } : {}),
      });
      setMensajes((m) => [
        ...m,
        {
          clave: `a-${Date.now()}`,
          rol: 'asistente',
          contenido: r.respuesta,
          fuentes: r.fuentes,
          pasos: r.pasos,
          rechazada: r.rechazada,
          tiempoMs: r.tiempo_ms,
          idEjecucion: r.id_ejecucion,
        },
      ]);
      setConversacionId(r.conversacion_id);
      void cliente.invalidateQueries({ queryKey: ['patroclo', 'conversaciones'] });
    } catch (e) {
      const p = pausaDe(e);
      if (p) {
        // Igual que en la pestaña: la pregunta vuelve a la caja y el aviso dice
        // cuánto esperar.
        setAhora(Date.now());
        setPausa(p);
        setMensajes((m) => m.slice(0, -1));
      } else {
        setMensajes((m) => [
          ...m,
          { clave: `e-${Date.now()}`, rol: 'error', contenido: e instanceof ErrorApi ? e.message : 'Ocurrió un error inesperado.' },
        ]);
      }
      setTexto(pregunta);
    } finally {
      setPensando(false);
      entrada.current?.focus();
    }
  };

  const alEnviar = (ev: FormEvent): void => {
    ev.preventDefault();
    void enviar(texto);
  };

  // Enter envía; Shift+Enter hace un salto de línea.
  const alTeclear = (ev: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      void enviar(texto);
    }
  };

  const nueva = (): void => {
    setConversacionId(undefined);
    setMensajes([]);
    setTexto('');
  };

  const abrirEnGrande = (): void => {
    setAbierto(false);
    void navegar(conversacionId ? `/patroclo/${conversacionId}` : '/patroclo');
  };

  return (
    <>
      {abierto && (
        <section
          role="dialog"
          aria-label="Chat con Patroclo"
          // En el teléfono ocupa la pantalla: una ventanita de 380 px no cabe y
          // un chat a medias es peor que uno entero.
          className="aparecer fixed inset-0 z-50 flex flex-col overflow-hidden bg-fondo shadow-flotante sm:inset-auto sm:bottom-24 sm:right-5 sm:h-[min(620px,calc(100vh-8rem))] sm:w-[400px] sm:rounded-2xl sm:border sm:border-borde"
        >
          <header className="flex items-center gap-3 bg-gradient-to-r from-marino to-acento px-4 py-3 text-white">
            <LogoPatroclo className="h-10 w-10 shrink-0 rounded-full ring-2 ring-white/25" />
            <div className="min-w-0 flex-1 leading-tight">
              <div className="font-titulo text-lg font-semibold">Patroclo</div>
              <div className="flex items-center gap-1.5 whitespace-nowrap text-xs text-white/70">
                <span className="h-1.5 w-1.5 rounded-full bg-lima" aria-hidden="true" />
                Asistente de Del Pastor
              </div>
            </div>
            <BotonCabecera etiqueta="Conversación nueva" alTocar={nueva}>
              <path d="M12 5v14M5 12h14" />
            </BotonCabecera>
            <BotonCabecera etiqueta="Abrir en grande" alTocar={abrirEnGrande}>
              <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" />
            </BotonCabecera>
            <BotonCabecera etiqueta="Cerrar" alTocar={() => setAbierto(false)}>
              <path d="M6 6l12 12M18 6 6 18" />
            </BotonCabecera>
          </header>

          <div className="flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite">
            {mensajes.length === 0 && (
              <div className="space-y-3 py-4 text-center">
                <LogoPatroclo className="mx-auto h-16 w-16" />
                <p className="font-titulo text-xl font-semibold text-marino">¿Qué quieres saber?</p>
                <p className="text-xs text-tenue">
                  Responde con los datos de ManejoAlcanza y dice de dónde sale cada cifra. Si no lo sabe, lo dice.
                </p>
                <div className="space-y-2 pt-1">
                  {SUGERENCIAS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void enviar(s)}
                      className="block min-h-[44px] w-full rounded-xl border border-borde bg-superficie px-3 py-2 text-left text-sm text-marino shadow-suave transition hover:border-acento/50 hover:bg-acento-tenue sm:min-h-0"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {mensajes.map((m) =>
              m.rol === 'asistente' ? (
                <MensajeAsistente
                  key={m.clave}
                  contenido={m.contenido}
                  fuentes={m.fuentes ?? []}
                  pasos={m.pasos ?? []}
                  rechazada={Boolean(m.rechazada)}
                  tiempoMs={m.tiempoMs ?? null}
                  idEjecucion={m.idEjecucion}
                />
              ) : m.rol === 'usuario' ? (
                <div
                  key={m.clave}
                  className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-acento px-3.5 py-2 text-white shadow-sm"
                >
                  {m.contenido}
                </div>
              ) : (
                <div key={m.clave} role="alert">
                  <Error_>{m.contenido}</Error_>
                </div>
              ),
            )}
            {pensando && <Pensando segundos={segundosPensando} />}
            <div ref={fin} />
          </div>

          {pausa && (
            <div className="px-4 pb-2">
              <Aviso>
                {pausa.mensaje}
                {pausa.hasta && pausa.tipo === 'espera' && (
                  <> Puedes volver a preguntar en {Math.max(1, Math.ceil((pausa.hasta - ahora) / 1000))} s.</>
                )}
                {pausa.tipo === 'no_disponible' && (
                  <>
                    {' '}
                    <button type="button" className="underline" onClick={() => setPausa(null)}>
                      Volver a intentar
                    </button>
                  </>
                )}
              </Aviso>
            </div>
          )}

          <form
            onSubmit={alEnviar}
            className="flex items-end gap-2 border-t border-borde bg-superficie p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          >
            <textarea
              ref={entrada}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={alTeclear}
              placeholder="Escríbele a Patroclo…"
              rows={1}
              maxLength={1000}
              disabled={pensando || pausa !== null}
              aria-label="Tu pregunta"
              className="max-h-32 min-h-[44px] flex-1 resize-none rounded-xl border border-borde bg-fondo px-3 py-2.5 text-base disabled:opacity-60 sm:text-sm"
            />
            <button
              type="submit"
              disabled={pensando || pausa !== null || !texto.trim()}
              aria-label="Enviar"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-acento text-white shadow-sm transition hover:bg-marino disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </form>
        </section>
      )}

      {/*
        El botón se queda aunque el chat esté abierto (en ancho): es también
        la forma de cerrarlo. En el teléfono el chat lo tapa y se cierra arriba.
      */}
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        aria-label={abierto ? 'Cerrar el chat con Patroclo' : 'Preguntarle a Patroclo'}
        className={`group fixed bottom-5 right-5 z-40 items-center gap-2 rounded-full bg-marino p-1.5 text-white shadow-flotante ring-4 ring-lima/30 transition hover:-translate-y-0.5 hover:ring-lima/60 ${
          abierto ? 'hidden sm:flex' : 'flex'
        }`}
      >
        <LogoPatroclo className="h-12 w-12" />
        <span className="hidden pr-3 text-sm font-medium sm:inline">{abierto ? 'Cerrar' : 'Pregúntale a Patroclo'}</span>
      </button>
    </>
  );
}

function Pensando({ segundos }: { segundos: number }): ReactNode {
  return (
    <div className="w-fit rounded-2xl rounded-bl-md border border-borde bg-superficie px-3.5 py-2.5 text-tenue shadow-suave">
      <span className="flex items-center gap-2">
        <span className="flex gap-1" aria-hidden="true">
          <span className="punto-pensando h-1.5 w-1.5 rounded-full bg-acento" />
          <span className="punto-pensando h-1.5 w-1.5 rounded-full bg-acento" />
          <span className="punto-pensando h-1.5 w-1.5 rounded-full bg-acento" />
        </span>
        Patroclo está pensando… <span className="cifra">{segundos} s</span>
      </span>
      {segundos >= 10 && <span className="mt-1 block text-xs">Una pregunta sobre los datos puede tardar hasta un minuto.</span>}
    </div>
  );
}

function BotonCabecera({
  etiqueta,
  alTocar,
  children,
}: {
  etiqueta: string;
  alTocar: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={alTocar}
      aria-label={etiqueta}
      title={etiqueta}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white/80 transition hover:bg-white/15 hover:text-white sm:h-9 sm:w-9"
    >
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
    </button>
  );
}
